import React, { useReducer, useCallback, useState } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { useDebouncedCallback } from 'use-debounce';

interface UserActivityState {
  lastActive: number;
  extendBoothStaffAvailability: boolean;
  videoActivity: null | unknown;
}

interface UserActivity {
  inputDeviceInteraction: () => void;
  videoActivity: (payload: unknown) => void;
  shouldExtendBoothStaffAvailability: (shouldSend: boolean) => void;
  state: UserActivityState;
  boothStaffAvailabilityHeartbeatStatus: null;
  setBoothStaffAvailabilityHeartbeatStatus: (status: unknown) => void;
}

// how often should the components with video players send the video activity
const VIDEO_ACTIVITY_FREQUENCY = 10 * 1000;
// how often should we get booth staff availability updates
const BOOTH_STAFF_ACTIVITY_FREQUENCY = 10 * 1000;
// the debounce delay for mouse/keyboard events
const DEVICE_INTERACTION_DEBOUNCE_DELAY = 2 * 1000;

const LOG = LoggerFactory.create('UserActivityContext');
const noOp = () => null;

const initialState: UserActivityState = {
  lastActive: Date.now(),
  extendBoothStaffAvailability: false,
  videoActivity: null
};

export const defaultContext: UserActivity = {
  inputDeviceInteraction: noOp,
  videoActivity: noOp,
  shouldExtendBoothStaffAvailability: noOp,
  state: initialState,
  boothStaffAvailabilityHeartbeatStatus: null,
  setBoothStaffAvailabilityHeartbeatStatus: noOp
};

const ReducerActions = Object.freeze({
  // e.g. mouse/keyboard interaction
  INPUT_DEVICE_ACTIVITY: 'INPUT_DEVICE_ACTIVITY',
  // user is active while watching session/exhibitor videos
  VIDEO_ACTIVITY: 'VIDEO_ACTIVITY',
  // reset video activity after heartbeat sent or video stopped playing
  CLEAR_VIDEO_ACTIVITY: 'CLEAR_VIDEO_ACTIVITY',
  // activity to extend the availability of booth staff while checked in
  EXTEND_BOOTH_STAFF_AVAILABILITY: 'EXTEND_BOOTH_STAFF_AVAILABILITY'
});

const reducer = (
  state: UserActivityState,
  action: { type: keyof typeof ReducerActions; payload?: any }
): UserActivityState => {
  const now = Date.now();
  let newState: UserActivityState;
  switch (action.type) {
    case ReducerActions.VIDEO_ACTIVITY: {
      const { isPlaying, ...rest } = action.payload;
      // set video activity to null if video stopped playing
      const videoActivity = isPlaying && Object.keys(rest).length > 0 ? { ...rest } : null;
      // if the video is not playing and last video activity is null then do not update state
      if (!isPlaying && state.videoActivity === null) {
        return state;
      }

      newState = {
        ...state,
        lastActive: now,
        videoActivity: isPlaying ? videoActivity : null
      };
      break;
    }
    case ReducerActions.CLEAR_VIDEO_ACTIVITY: {
      // if video activity is already null just return the existing state
      if (state.videoActivity === null) {
        return state;
      }
      newState = {
        ...state,
        videoActivity: null
      };
      break;
    }
    case ReducerActions.EXTEND_BOOTH_STAFF_AVAILABILITY: {
      // lets not update the state if we're not still extending the booth staff availability
      if (!action.payload && !state.extendBoothStaffAvailability) {
        return state;
      }
      newState = {
        ...state,
        lastActive: now,
        extendBoothStaffAvailability: action.payload
      };
      break;
    }
    case ReducerActions.INPUT_DEVICE_ACTIVITY: {
      newState = {
        ...state,
        lastActive: now
      };
      break;
    }
    default:
      newState = state;
      break;
  }

  LOG.debug('State updated: ', {
    currentState: {
      ...newState,
      lastActive: new Date(newState.lastActive)
    },
    previousState: {
      ...state,
      lastActive: new Date(state.lastActive)
    },
    action
  });

  return newState;
};

const UserActivityContext = React.createContext<UserActivity>(defaultContext);

/**
 * Keeps track of when user was last active in the app and send the heartbeat mutation on set intervals.
 * User can be active when interacting with the app via mouse/keyboard or when watching a video.
 * The heartbeat is used to extend auth token (session token), publish heartbeat encounters while user is watching
 * session video, and to extend the exhibitor booth staff availability document while booth staff is checked in
 */
function UserActivityContextProvider({ children }: { children?: React.ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [boothStaffAvailabilityHeartbeatStatus, setBoothStaffAvailabilityHeartbeatStatus] = useState(null);

  // use debouncing to update the state since mouse/keyboard events fire too often
  const inputDeviceInteraction = useDebouncedCallback(() => {
    dispatch({
      type: ReducerActions.INPUT_DEVICE_ACTIVITY
    });
  }, DEVICE_INTERACTION_DEBOUNCE_DELAY);

  const shouldExtendBoothStaffAvailability = useCallback(
    shouldSend =>
      dispatch({
        type: ReducerActions.EXTEND_BOOTH_STAFF_AVAILABILITY,
        payload: shouldSend
      }),
    [dispatch]
  );

  const videoActivity = useCallback(
    payload =>
      dispatch({
        type: ReducerActions.VIDEO_ACTIVITY,
        payload
      }),
    [dispatch]
  );

  const contextValue = React.useMemo<UserActivity>(
    () => ({
      inputDeviceInteraction,
      videoActivity,
      shouldExtendBoothStaffAvailability,
      state,
      setBoothStaffAvailabilityHeartbeatStatus,
      boothStaffAvailabilityHeartbeatStatus
    }),
    [
      inputDeviceInteraction,
      videoActivity,
      shouldExtendBoothStaffAvailability,
      state,
      setBoothStaffAvailabilityHeartbeatStatus,
      boothStaffAvailabilityHeartbeatStatus
    ]
  );

  return <UserActivityContext.Provider value={contextValue}>{children}</UserActivityContext.Provider>;
}

export default UserActivityContextProvider;

export const useUserActivityContext = () => React.useContext(UserActivityContext);

export {
  reducer,
  UserActivityContext,
  VIDEO_ACTIVITY_FREQUENCY,
  BOOTH_STAFF_ACTIVITY_FREQUENCY,
  DEVICE_INTERACTION_DEBOUNCE_DELAY
};
